import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
    Avatar,
    Box,
    CircularProgress,
    Container,
    Divider,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RecipeImage from '../components/RecipeImage';
import { RECIPE_TYPES } from '../constants/Constants';

const UserProfilePage = () => {
    const { user_id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recipeFilter, setRecipeFilter] = useState('');

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/users/${user_id}/recipes`, {
            headers: { 'Authorization': 'Bearer ' + Cookies.get('token') },
        })
            .then(r => r.json())
            .then(setData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [user_id]);

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
            <CircularProgress />
        </Box>
    );

    if (!data?.user) return (
        <Container sx={{ mt: 5 }}>
            <Typography color="text.secondary">User not found.</Typography>
        </Container>
    );

    const { user, recipes } = data;
    const displayName = user.display_name || `${user.first_name} ${user.last_name}`.trim();
    const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();

    return (
        <Container component="main" sx={{ mt: 5, width: '90%', pb: 12 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, gap: 3, mb: 3 }}>
                <Avatar
                    src={user.avatar_url || undefined}
                    sx={{ width: 120, height: 120, bgcolor: 'primary.main', fontSize: '2.5rem', flexShrink: 0 }}
                >
                    {initials}
                </Avatar>
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{displayName}</Typography>
                    {user.display_name && (
                        <Typography variant="body2" color="text.secondary">
                            {user.first_name} {user.last_name}
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        @{user.username}
                    </Typography>
                    {user.bio && (
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, maxWidth: 480 }}>
                            {user.bio}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, flex: '1 1 auto' }}>
                    Recipes by {displayName}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({recipes.length})
                    </Typography>
                </Typography>
                {recipes.length > 0 && (
                    <TextField
                        size="small"
                        placeholder="Filter recipes…"
                        value={recipeFilter}
                        onChange={e => setRecipeFilter(e.target.value)}
                        sx={{ width: { xs: '100%', sm: 220 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </Box>

            {recipes.length === 0 ? (
                <Typography color="text.secondary">No recipes yet.</Typography>
            ) : (
                <List disablePadding>
                    {recipes.filter(r => r.title.toLowerCase().includes(recipeFilter.toLowerCase())).map((recipe) => {
                        const type = RECIPE_TYPES.find(t => t.id === recipe.recipe_type_id);
                        return (
                            <ListItem key={recipe.id} disablePadding divider>
                                <ListItemButton component={Link} to={`/recipe/${recipe.id}`} state={{ id: recipe.id }} sx={{ gap: 1.5 }}>
                                    <RecipeImage
                                        loading="lazy"
                                        src={recipe.download_image_url || '/anchovie.jpg'}
                                        alt={recipe.title}
                                        sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0 }}
                                    />
                                    <ListItemText
                                        primary={recipe.title}
                                        secondary={type ? `${type.icon} ${type.title}` : null}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Container>
    );
};

export default UserProfilePage;
